import { useState } from 'react';
import { validate, validateAll } from 'helpers/validation';

function stateFactory(fields) {
    return Object.keys(fields).reduce((acc, key) => {
        acc[key] = {
            value: fields[key],
            meta: {
                touched: false,
                dirty: false,
            },
        };
        return acc;
    }, {});
}

function getDataFromState(state) {
    return Object.keys(state).reduce((acc, key) => {
        acc[key] = state[key].value;
        return acc;
    }, {});
}

function extendsValidations(key, validation, newErrors = []) {
    const newValidation = {
        errors: {
            ...validation.errors,
            [key]: newErrors,
        },
    };

    newValidation['valid'] = Object.keys(newValidation.errors).every(
        (errorKey) => {
            const error = newValidation.errors[errorKey];
            return error === null || error.length === 0;
        },
    );

    return newValidation;
}

function onBlurHandlerByKey(
    state,
    key,
    setState,
    setValidation,
    validation,
    rulesBy,
    customErrors,
) {
    return (event) => {
        const { value } = event.currentTarget;
        const newState = getDataFromState(state);

        if (newState[key] && newState[key].length) {
            const newErrors = validate(
                value,
                rulesBy[key],
                newState,
                key,
                customErrors,
            );
            setValidation(
                extendsValidations(
                    key,
                    validation,
                    newErrors ? newErrors.message : null,
                ),
            );
        } else {
            setValidation(extendsValidations(key, validation, ''));
        }
    };
}

function onChangeHandlerByKey(
    state,
    key,
    setState,
    setValidation,
    validation,
    rulesBy,
    customErrors,
) {
    return (event) => {
        const { value } = event.currentTarget;
        const newState = {
            ...state,
            [key]: {
                ...state[key],
                value: value,
                meta: {
                    ...state[key].meta,
                    dirty: true,
                },
            },
        };

        setState(newState);

        if (validation.errors[key] && value.length) {
            const newErrors = validate(
                value,
                rulesBy[key],
                getDataFromState(state),
                key,
                customErrors,
            );
            setValidation(
                extendsValidations(
                    key,
                    validation,
                    newErrors ? newErrors.message : null,
                ),
            );
        } else {
            setValidation(extendsValidations(key, validation, ''));
        }
    };
}

function onClickHandlerByKey(state, key, setState) {
    return () => {
        setState({
            ...state,
            [key]: {
                ...state[key],
                meta: {
                    ...state[key].meta,
                    touched: true,
                },
            },
        });
    };
}

function formDataFactory(
    state,
    setState,
    setValidation,
    validation,
    rulesBy,
    customErrors,
) {
    return Object.keys(state).reduce((acc, key) => {
        acc[key] = {
            meta: state[key].meta,
            input: {
                value: state[key].value,
                onClick: onClickHandlerByKey(
                    state,
                    key,
                    setState,
                    setValidation,
                    validation,
                    rulesBy,
                ),
                onChange: onChangeHandlerByKey(
                    state,
                    key,
                    setState,
                    setValidation,
                    validation,
                    rulesBy,
                    customErrors,
                ),
                onBlur: onBlurHandlerByKey(
                    state,
                    key,
                    setState,
                    setValidation,
                    validation,
                    rulesBy,
                    customErrors,
                ),
            },
        };
        return acc;
    }, {});
}

export const useValidatedForm = (
    fields = {},
    rulesBy = {},
    customErrors = {},
) => {
    const initialState = stateFactory(fields);
    const [state, setState] = useState(initialState);
    const [validation, setValidation] = useState({ valid: true, errors: {} });
    const form = formDataFactory(
        state,
        setState,
        setValidation,
        validation,
        rulesBy,
        customErrors,
    );

    const getData = () => getDataFromState(state);
    const setData = (data) => setState(stateFactory(data));
    const setError = (key, error) => {
        setValidation({
            ...validation,
            errors: {
                [key]: error,
            },
        });
    };

    const validate = () => {
        const newValidations = validateAll(
            getDataFromState(state),
            rulesBy,
            customErrors,
        );
        setValidation(newValidations);

        return newValidations.valid;
    };

    return {
        form,
        errors: validation.errors,
        validate,
        getData,
        setData,
        setError,
    };
};
